import { useEffect, useState } from 'react';

interface NewsItem {
  id: string;
  title: string;
  publisher: string;
  url: string;
  tickers: string[];
  publishedAt: string;
}

// Keyword → emoji, first match wins (checked in order)
const EMOJI_RULES: Array<[RegExp, string]> = [
  [/merger|acquisit|acquire|takeover|buyout|\bdeal\b/i, '🤝'],
  [/earnings|quarter|results|revenue|\beps\b|profit/i, '📊'],
  [/fed\b|fomc|rate[s]?\b|inflation|cpi|treasury|yields/i, '🏦'],
  [/lawsuit|court|sec charges|probe|investigat|fraud|settle/i, '⚖️'],
  [/ipo|public offering|listing|debut/i, '🔔'],
  [/\bai\b|artificial intelligence|chatbot|llm/i, '🤖'],
  [/bitcoin|crypto|ethereum|blockchain|token/i, '🪙'],
  [/oil|opec|energy|gas prices|crude/i, '🛢️'],
  [/dividend|buyback|repurchase/i, '💰'],
  [/upgrade|downgrade|price target|initiat.*coverage/i, '🎯'],
  [/guidance|forecast|outlook|projects/i, '🔮'],
  [/layoff|job cuts|restructur/i, '✂️'],
  [/surge|soar|jump|rally|record high|all-time high|beats/i, '🚀'],
  [/fall|drop|plunge|slump|sink|miss|cuts|tumble|crash/i, '📉'],
  [/chip|semiconductor|foundry/i, '💾'],
];

function emojiFor(title: string): string {
  for (const [pattern, emoji] of EMOJI_RULES) {
    if (pattern.test(title)) return emoji;
  }
  return '📰';
}

function age(iso: string): string {
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export function NewsFeed() {
  const [items, setItems] = useState<NewsItem[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch('/api/news', { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setItems(data.items || []);
        setError(false);
      } catch (e) {
        console.error('Error fetching news:', e);
        setError(true);
      } finally {
        clearTimeout(timeout);
      }
    };
    load();
    const interval = setInterval(load, 300000);
    return () => clearInterval(interval);
  }, []);

  if (error && !items) return <div className="empty-state">News wire unavailable</div>;
  if (!items) return <div className="loading">Loading news...</div>;
  if (items.length === 0) return <div className="empty-state">No headlines right now</div>;

  return (
    <div className="news-feed">
      {items.map((item) => (
        <a className="news-item" key={item.id} href={item.url} target="_blank" rel="noopener noreferrer">
          <span className="news-age">{age(item.publishedAt)}</span>
          <span className="news-emoji">{emojiFor(item.title)}</span>
          <span className="news-title">{item.title}</span>
          <span className="news-meta">
            {item.tickers.map((t) => (
              <span className="news-ticker" key={t}>
                {t}
              </span>
            ))}
            <span className="news-publisher">{item.publisher}</span>
          </span>
        </a>
      ))}
    </div>
  );
}
